import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowUpRight,
  Award,
  DollarSign,
  Download,
  Loader2,
  Plus,
  Trash2,
  Users,
  Utensils,
} from "lucide-react";
import {
  api,
  type AdminCompetitionEntry,
  type AdminDonationEntry,
  type AdminPotluckItem,
  type AdminRsvpEntry,
  type AdminStats,
  type DonationSource,
  type ManualDonationMethod,
} from "@/lib/api-client";
import { seededEvents } from "@/data/seed-imam-ali-2026";
import type { Event } from "@/lib/event-schema";
import { NotFoundView } from "@/routes/NotFound";
import { cn } from "@/lib/cn";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; event: Event; stats: AdminStats }
  | { status: "not-found" };

export function AdminEventDashboard() {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [warning, setWarning] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setState({ status: "not-found" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    Promise.all([api.events.get(slug), api.admin.stats(slug)])
      .then(([event, stats]) => {
        if (cancelled) return;
        setState({ status: "ready", event, stats });
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Failed to refresh.";
        // A 401 from the admin stats endpoint means the session has
        // expired. Don't masquerade as a 404; tell the user what to do.
        if (/^401\b/.test(message)) {
          setWarning("Your admin session has expired. Sign in again to refresh stats.");
        } else {
          setWarning(message);
        }
        const seed: Event | undefined = seededEvents[slug];
        if (seed) {
          setState({
            status: "ready",
            event: seed,
            stats: {
              rsvp: { families: 0, adults: 0, children: 0, attendees: 0 },
              potluck: { dishes: 0, byCategory: {} },
              competition: { entries: 0 },
              donations: { totalCents: 0, manualCents: 0, stripeCents: 0, manualCount: 0 },
            },
          });
        } else {
          setState({ status: "not-found" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function refreshStats() {
    if (state.status !== "ready") return;
    try {
      const stats = await api.admin.stats(state.event.slug);
      setState({ status: "ready", event: state.event, stats });
    } catch (err) {
      console.error("stats refresh failed", err);
    }
  }

  if (state.status === "loading") {
    return (
      <div className="grid h-96 place-items-center text-muted">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.75} />
      </div>
    );
  }
  if (state.status === "not-found") return <NotFoundView />;
  const { event, stats } = state;

  return (
    <div className="mx-auto w-full max-w-6xl">
      <header className="mb-8">
        <Link
          to={`/admin/events/${event.slug}`}
          className="text-xs uppercase tracking-[0.18em] text-muted hover:text-accent"
        >
          ← Editor
        </Link>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="display-title text-[clamp(2rem,4vw,3rem)] leading-tight">
              {event.title}
            </h1>
            <p className="mt-1 text-sm text-muted">Submissions and counts</p>
          </div>
          <Link
            to={`/${event.slug}`}
            target="_blank"
            className="inline-flex items-center gap-1.5 rounded-full border border-line/70 px-4 py-2 text-sm text-ink hover:border-accent hover:text-accent"
          >
            Open public
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
          </Link>
        </div>
        {warning ? (
          <p className="mt-3 text-xs text-red-600 dark:text-red-400">
            Couldn't refresh: {warning}
          </p>
        ) : null}
      </header>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5 mb-12">
        <StatCard
          label="Donations"
          value={formatDollars(stats.donations.totalCents)}
          sub={
            stats.donations.totalCents
              ? `Stripe ${formatDollars(stats.donations.stripeCents)} · off-channel ${formatDollars(stats.donations.manualCents)}`
              : "No donations yet"
          }
          icon={<DollarSign className="h-4 w-4" strokeWidth={1.75} />}
        />
        <StatCard
          label="Families"
          value={stats.rsvp.families}
          sub={`${stats.rsvp.attendees} attendees`}
          icon={<Users className="h-4 w-4" strokeWidth={1.75} />}
        />
        <StatCard
          label="Adults"
          value={stats.rsvp.adults}
          sub={`${stats.rsvp.children} children`}
          icon={<Users className="h-4 w-4" strokeWidth={1.75} />}
        />
        <StatCard
          label="Potluck dishes"
          value={stats.potluck.dishes}
          sub={`${Object.keys(stats.potluck.byCategory).length} categories`}
          icon={<Utensils className="h-4 w-4" strokeWidth={1.75} />}
        />
        <StatCard
          label="Competition entries"
          value={stats.competition.entries}
          icon={<Award className="h-4 w-4" strokeWidth={1.75} />}
        />
      </section>

      <RsvpTable slug={event.slug} onChange={refreshStats} />
      <div className="h-12" />
      <PotluckTable slug={event.slug} onChange={refreshStats} />
      <div className="h-12" />
      <CompetitionTable event={event} onChange={refreshStats} />
      <div className="h-12" />
      <ManualDonationsPanel slug={event.slug} onChange={refreshStats} />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  icon,
}: {
  label: string;
  value: number | string;
  sub?: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="surface-card relative overflow-hidden p-5">
      <div
        aria-hidden
        className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-accent/10 blur-2xl"
      />
      <div className="flex items-center gap-2 text-accent">
        {icon}
        <span className="eyebrow">{label}</span>
      </div>
      <p className="mt-3 font-display text-3xl font-light leading-none text-ink">
        {value}
      </p>
      {sub ? <p className="mt-1.5 text-xs text-muted">{sub}</p> : null}
    </div>
  );
}

function SectionHeader({
  title,
  count,
  onExport,
}: {
  title: string;
  count: number;
  onExport?: () => void;
}) {
  return (
    <header className="mb-4 flex items-end justify-between gap-3 flex-wrap">
      <div>
        <h2 className="font-display text-2xl font-light text-ink">{title}</h2>
        <p className="mt-1 text-xs text-subtle">
          {count} {count === 1 ? "row" : "rows"}
        </p>
      </div>
      {onExport ? (
        <button
          type="button"
          onClick={onExport}
          disabled={count === 0}
          className="inline-flex items-center gap-1.5 rounded-full border border-line/70 px-3.5 py-1.5 text-xs uppercase tracking-[0.14em] text-muted hover:border-accent hover:text-accent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="h-3 w-3" strokeWidth={1.75} />
          Export CSV
        </button>
      ) : null}
    </header>
  );
}

function RsvpTable({
  slug,
  onChange,
}: {
  slug: string;
  onChange: () => Promise<void>;
}) {
  const [rows, setRows] = useState<AdminRsvpEntry[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.admin.rsvp
      .list(slug)
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load.");
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function remove(row: AdminRsvpEntry) {
    if (!confirm(`Delete RSVP for ${row.familyName}? This can't be undone.`)) return;
    setBusy(row.id);
    setError(null);
    try {
      await api.admin.rsvp.remove(slug, row.id);
      setRows((prev) => (prev ? prev.filter((r) => r.id !== row.id) : prev));
      await onChange();
    } catch (err) {
      setError(humanizeDeleteError(err));
    } finally {
      setBusy(null);
    }
  }

  function exportCsv() {
    if (!rows) return;
    const header = ["familyName", "adults", "children", "childrenAges", "timestamp"]
      .map(csv)
      .join(",");
    const lines = rows.map((r) =>
      [
        csv(r.familyName),
        r.adults,
        r.children,
        csv((r.childrenAges ?? []).filter(Boolean).join("; ")),
        new Date(r.timestamp).toISOString(),
      ].join(","),
    );
    downloadCsv(`${slug}-rsvp.csv`, [header, ...lines].join("\r\n"));
  }

  return (
    <section>
      <SectionHeader title="RSVPs" count={rows?.length ?? 0} onExport={exportCsv} />
      <Table>
        <THead cols={["Family", "Adults", "Children (ages)", "Submitted", ""]} />
        <tbody>
          {error ? (
            <ErrorRow text={error} cols={5} />
          ) : rows === null ? (
            <SkeletonRow cols={5} />
          ) : rows.length === 0 ? (
            <EmptyRow text="No RSVPs yet." cols={5} />
          ) : (
            rows.map((row) => (
              <tr key={row.id} className="border-t border-line/60">
                <Td>{row.familyName}</Td>
                <Td className="font-mono">{row.adults}</Td>
                <Td className="font-mono">
                  {row.children}
                  {row.childrenAges?.some(Boolean) ? (
                    <span className="ml-2 font-sans text-xs text-muted">
                      ({row.childrenAges.filter(Boolean).join(", ")})
                    </span>
                  ) : null}
                </Td>
                <Td className="text-muted">{relativeTime(row.timestamp)}</Td>
                <Td className="text-right">
                  <DeleteButton
                    onClick={() => remove(row)}
                    busy={busy === row.id}
                    label={`Delete RSVP for ${row.familyName}`}
                  />
                </Td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </section>
  );
}

function PotluckTable({
  slug,
  onChange,
}: {
  slug: string;
  onChange: () => Promise<void>;
}) {
  const [rows, setRows] = useState<AdminPotluckItem[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.admin.potluck
      .list(slug)
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load.");
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function remove(row: AdminPotluckItem) {
    if (!confirm(`Delete "${row.name}" by ${row.contributorName}? This can't be undone.`)) return;
    setBusy(row.id);
    setError(null);
    try {
      await api.admin.potluck.remove(slug, row.id);
      setRows((prev) => (prev ? prev.filter((r) => r.id !== row.id) : prev));
      await onChange();
    } catch (err) {
      setError(humanizeDeleteError(err));
    } finally {
      setBusy(null);
    }
  }

  function exportCsv() {
    if (!rows) return;
    const header = ["dish", "contributor", "category", "quantity", "notes", "timestamp"]
      .map(csv)
      .join(",");
    const lines = rows.map((r) =>
      [
        csv(r.name),
        csv(r.contributorName),
        csv(r.category),
        csv(r.quantity),
        csv(r.notes ?? ""),
        new Date(r.timestamp).toISOString(),
      ].join(","),
    );
    downloadCsv(`${slug}-potluck.csv`, [header, ...lines].join("\r\n"));
  }

  return (
    <section>
      <SectionHeader title="Potluck" count={rows?.length ?? 0} onExport={exportCsv} />
      <Table>
        <THead cols={["Dish", "By", "Category", "Quantity", "Submitted", ""]} />
        <tbody>
          {error ? (
            <ErrorRow text={error} cols={6} />
          ) : rows === null ? (
            <SkeletonRow cols={6} />
          ) : rows.length === 0 ? (
            <EmptyRow text="No dishes yet." cols={6} />
          ) : (
            rows.map((row) => (
              <tr key={row.id} className="border-t border-line/60">
                <Td className="font-medium">{row.name}</Td>
                <Td>{row.contributorName}</Td>
                <Td className="text-muted">{row.category}</Td>
                <Td className="font-mono">{row.quantity}</Td>
                <Td className="text-muted">{relativeTime(row.timestamp)}</Td>
                <Td className="text-right">
                  <DeleteButton
                    onClick={() => remove(row)}
                    busy={busy === row.id}
                    label={`Delete dish ${row.name} by ${row.contributorName}`}
                  />
                </Td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </section>
  );
}

function CompetitionTable({
  event,
  onChange,
}: {
  event: Event;
  onChange: () => Promise<void>;
}) {
  const [rows, setRows] = useState<AdminCompetitionEntry[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fields = event.modules.competition?.fields ?? [];

  useEffect(() => {
    let cancelled = false;
    api.admin.competition
      .list(event.slug)
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load.");
      });
    return () => {
      cancelled = true;
    };
  }, [event.slug]);

  async function remove(row: AdminCompetitionEntry) {
    const summary = pickEntryLabel(row, fields);
    if (!confirm(`Delete entry ${summary}? This can't be undone.`)) return;
    setBusy(row.id);
    setError(null);
    try {
      await api.admin.competition.remove(event.slug, row.id);
      setRows((prev) => (prev ? prev.filter((r) => r.id !== row.id) : prev));
      await onChange();
    } catch (err) {
      setError(humanizeDeleteError(err));
    } finally {
      setBusy(null);
    }
  }

  const columns = useMemo(() => {
    if (fields.length > 0) return fields;
    // Fallback: derive columns from the first row's keys (admin entries
    // may have free-form fields when no schema is configured yet). Skip
    // internal + private fields so passcodes / contact data can't leak
    // into the table or CSV.
    if (rows && rows.length > 0) {
      const deny = new Set(["id", "timestamp", "passcode", "password"]);
      const keys = Object.keys(rows[0]).filter((k) => !deny.has(k));
      return keys.map((id) => ({
        id,
        label: id.replace(/[_-]+/g, " ").replace(/([A-Z])/g, " $1").replace(/^./, (c) =>
          c.toUpperCase(),
        ).trim(),
        type: "text" as const,
      }));
    }
    return [];
  }, [fields, rows]);

  function exportCsv() {
    if (!rows) return;
    const headerCols = [...columns.map((c) => c.label), "Submitted"];
    const header = headerCols.map(csv).join(",");
    const lines = rows.map((row) =>
      [
        ...columns.map((c) => csv(stringify(row[c.id]))),
        new Date(row.timestamp).toISOString(),
      ].join(","),
    );
    downloadCsv(`${event.slug}-competition.csv`, [header, ...lines].join("\r\n"));
  }

  return (
    <section>
      <SectionHeader
        title="Competition entries"
        count={rows?.length ?? 0}
        onExport={exportCsv}
      />
      <Table>
        <THead cols={[...columns.map((c) => c.label), "Submitted", ""]} />
        <tbody>
          {error ? (
            <ErrorRow text={error} cols={columns.length + 2} />
          ) : rows === null ? (
            <SkeletonRow cols={columns.length + 2} />
          ) : rows.length === 0 ? (
            <EmptyRow text="No entries yet." cols={columns.length + 2} />
          ) : (
            rows.map((row) => (
              <tr key={row.id} className="border-t border-line/60">
                {columns.map((col) => (
                  <Td key={col.id} className={col.type === "boolean" ? "font-mono" : ""}>
                    {renderCell(row[col.id], col.type)}
                  </Td>
                ))}
                <Td className="text-muted">{relativeTime(row.timestamp)}</Td>
                <Td className="text-right">
                  <DeleteButton
                    onClick={() => remove(row)}
                    busy={busy === row.id}
                    label={`Delete entry ${pickEntryLabel(row, fields)}`}
                  />
                </Td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </section>
  );
}

function Table({ children }: { children: React.ReactNode }) {
  return (
    <div className="surface-card overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-sm">{children}</table>
    </div>
  );
}

function THead({ cols }: { cols: string[] }) {
  return (
    <thead>
      <tr className="bg-paper text-left">
        {cols.map((c, i) => (
          <th
            key={i}
            scope="col"
            className="px-4 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-subtle"
          >
            {c}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={cn("px-4 py-3 text-ink align-top", className)}>{children}</td>;
}

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-6">
        <div className="h-3 w-3/4 animate-pulse rounded bg-line/70" />
      </td>
    </tr>
  );
}

function EmptyRow({ text, cols }: { text: string; cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm text-muted">
        {text}
      </td>
    </tr>
  );
}

function ErrorRow({ text, cols }: { text: string; cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-6 text-center text-sm text-red-600 dark:text-red-400">
        {text}
      </td>
    </tr>
  );
}

function DeleteButton({
  onClick,
  busy,
  label,
}: {
  onClick: () => void;
  busy: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-label={label ?? "Delete row"}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors",
        "border-line/70 text-muted hover:border-red-400 hover:text-red-600 dark:hover:text-red-400",
        busy && "opacity-60 cursor-wait",
      )}
    >
      {busy ? (
        <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.75} />
      ) : (
        <Trash2 className="h-3 w-3" strokeWidth={1.75} />
      )}
      Delete
    </button>
  );
}

function humanizeDeleteError(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  if (/^401\b/.test(message)) {
    return "Your admin session expired. Sign in again, then retry.";
  }
  if (/^404\b/.test(message)) {
    return "Already deleted by someone else. Refresh to see the latest.";
  }
  return message || "Delete failed.";
}

function pickEntryLabel(
  row: Record<string, unknown>,
  fields: ReadonlyArray<{ id: string; type: "text" | "longtext" | "boolean" }>,
): string {
  // Prefer the first text-typed field's value as a human label.
  for (const field of fields) {
    if (field.type === "boolean") continue;
    const value = row[field.id];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  // Otherwise fall back to a short id.
  const id = typeof row.id === "string" ? row.id : "";
  return id ? id.slice(0, 8) : "this entry";
}

function renderCell(value: unknown, type: "text" | "longtext" | "boolean") {
  if (type === "boolean") {
    return value ? "✓" : "·";
  }
  if (value == null) return <span className="text-subtle">—</span>;
  const text = String(value);
  if (type === "longtext" && text.length > 80) {
    return <span title={text}>{text.slice(0, 80)}…</span>;
  }
  return text;
}

function stringify(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

function formatDollars(cents: number): string {
  const dollars = Math.round(cents) / 100;
  return `$${dollars.toLocaleString(undefined, {
    minimumFractionDigits: dollars >= 1000 || Number.isInteger(dollars) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

function csv(s: string): string {
  if (s == null) return "";
  const t = String(s);
  if (/[",\n]/.test(t)) return `"${t.replaceAll('"', '""')}"`;
  return t;
}

function downloadCsv(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const METHOD_LABELS: Record<ManualDonationMethod, string> = {
  zelle: "Zelle",
  cash: "Cash",
  bank: "Bank transfer",
  check: "Check",
  other: "Other",
};

const SOURCE_LABELS: Record<DonationSource, string> = {
  stripe: "Stripe",
  ...METHOD_LABELS,
};

function ManualDonationsPanel({
  slug,
  onChange,
}: {
  slug: string;
  onChange: () => Promise<void>;
}) {
  const [rows, setRows] = useState<AdminDonationEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<ManualDonationMethod>("zelle");
  const [donorName, setDonorName] = useState("");
  const [note, setNote] = useState("");
  const [occurredOn, setOccurredOn] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function refresh() {
    const fresh = await api.admin.donations.list(slug);
    setRows(fresh);
  }

  useEffect(() => {
    let cancelled = false;
    api.admin.donations
      .list(slug)
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load.");
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const totalCents = (rows ?? []).reduce((sum, r) => sum + r.amountCents, 0);
  const stripeCents = (rows ?? [])
    .filter((r) => r.source === "stripe")
    .reduce((sum, r) => sum + r.amountCents, 0);
  const offChannelCents = totalCents - stripeCents;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const dollars = Number.parseFloat(amount);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      setSubmitError("Enter a positive amount.");
      return;
    }
    setSubmitting(true);
    try {
      await api.admin.donations.add(slug, {
        amountCents: Math.round(dollars * 100),
        method,
        donorName: donorName.trim() || undefined,
        note: note.trim() || undefined,
        occurredOn: occurredOn || undefined,
      });
      await refresh();
      setAmount("");
      setDonorName("");
      setNote("");
      setOccurredOn("");
      await onChange();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to record donation.");
    } finally {
      setSubmitting(false);
    }
  }

  async function remove(row: AdminDonationEntry) {
    if (!row.deletable) return;
    const label = row.donorName ?? SOURCE_LABELS[row.source];
    if (!confirm(`Remove $${(row.amountCents / 100).toFixed(2)} from ${label}?`)) return;
    setBusy(row.id);
    try {
      await api.admin.donations.remove(slug, row.id);
      setRows((prev) => (prev ? prev.filter((r) => r.id !== row.id) : prev));
      await onChange();
    } catch (err) {
      setError(humanizeDeleteError(err));
    } finally {
      setBusy(null);
    }
  }

  function exportCsv() {
    if (!rows) return;
    const header = ["date", "source", "status", "donor", "amount_usd", "note", "timestamp"]
      .map(csv)
      .join(",");
    const lines = rows.map((r) =>
      [
        csv(r.occurredOn ?? new Date(r.timestamp).toISOString().slice(0, 10)),
        csv(SOURCE_LABELS[r.source]),
        csv(r.status ?? ""),
        csv(r.donorName ?? ""),
        (r.amountCents / 100).toFixed(2),
        csv(r.note ?? ""),
        new Date(r.timestamp).toISOString(),
      ].join(","),
    );
    downloadCsv(`${slug}-donations.csv`, [header, ...lines].join("\r\n"));
  }

  return (
    <section>
      <SectionHeader
        title="Donations"
        count={rows?.length ?? 0}
        onExport={rows && rows.length ? exportCsv : undefined}
      />
      <p className="-mt-2 mb-4 text-xs text-subtle">
        Total <span className="font-mono text-ink">{formatDollars(totalCents)}</span>
        {" — "}
        Stripe <span className="font-mono text-ink">{formatDollars(stripeCents)}</span>
        {" · off-channel "}
        <span className="font-mono text-ink">{formatDollars(offChannelCents)}</span>.
        Add a row below to log a Zelle, cash, or bank transfer gift; Stripe payments are added
        automatically.
      </p>

      <form
        onSubmit={submit}
        className="surface-card mb-6 grid gap-3 p-4 sm:grid-cols-[120px,140px,1fr,140px,auto]"
      >
        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.14em] text-subtle">Amount</span>
          <div className="mt-1 inline-flex w-full items-center gap-1 rounded-xl border border-line/70 bg-surface px-3 py-2 focus-within:border-accent">
            <DollarSign className="h-3.5 w-3.5 text-subtle" strokeWidth={1.75} />
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              placeholder="100"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-transparent text-sm outline-none"
              required
            />
          </div>
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.14em] text-subtle">Method</span>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as ManualDonationMethod)}
            className="mt-1 w-full rounded-xl border border-line/70 bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
          >
            {(Object.keys(METHOD_LABELS) as ManualDonationMethod[]).map((m) => (
              <option key={m} value={m}>
                {METHOD_LABELS[m]}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.14em] text-subtle">
            Donor (optional)
          </span>
          <input
            type="text"
            placeholder="Tahiri family"
            value={donorName}
            onChange={(e) => setDonorName(e.target.value)}
            className="mt-1 w-full rounded-xl border border-line/70 bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
          />
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.14em] text-subtle">
            Date received
          </span>
          <input
            type="date"
            value={occurredOn}
            onChange={(e) => setOccurredOn(e.target.value)}
            className="mt-1 w-full rounded-xl border border-line/70 bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
          />
        </label>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={submitting}
            className={cn(
              "inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-medium text-canvas",
              "dark:bg-accent dark:text-accent-on",
              "hover:bg-ink/90 dark:hover:bg-accent/90 disabled:opacity-60",
            )}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
            ) : (
              <Plus className="h-4 w-4" strokeWidth={1.75} />
            )}
            Add
          </button>
        </div>

        <label className="block sm:col-span-5">
          <span className="block text-[11px] uppercase tracking-[0.14em] text-subtle">
            Note (optional)
          </span>
          <input
            type="text"
            placeholder="Zelle reference 4123, dropped off at Friday prayer, etc."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="mt-1 w-full rounded-xl border border-line/70 bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
          />
        </label>

        {submitError ? (
          <p className="sm:col-span-5 text-sm text-red-600 dark:text-red-400">{submitError}</p>
        ) : null}
      </form>

      <Table>
        <THead cols={["Date", "Amount", "Source", "Donor", "Note", ""]} />
        <tbody>
          {error ? (
            <ErrorRow text={error} cols={6} />
          ) : rows === null ? (
            <SkeletonRow cols={6} />
          ) : rows.length === 0 ? (
            <EmptyRow text="No donations recorded yet." cols={6} />
          ) : (
            rows.map((row) => {
              const refunded = row.status === "refunded";
              return (
                <tr key={row.id} className="border-t border-line/60">
                  <Td className="text-muted">
                    {row.occurredOn ?? new Date(row.timestamp).toISOString().slice(0, 10)}
                  </Td>
                  <Td
                    className={cn(
                      "font-mono",
                      row.amountCents < 0 ? "text-red-600 dark:text-red-400" : "text-ink",
                    )}
                  >
                    {row.amountCents < 0 ? "-" : ""}
                    {formatDollars(Math.abs(row.amountCents))}
                  </Td>
                  <Td>
                    <span className="inline-flex items-center gap-1.5">
                      {SOURCE_LABELS[row.source]}
                      {refunded ? (
                        <span className="rounded-full bg-red-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] text-red-600 dark:text-red-400">
                          Refund
                        </span>
                      ) : null}
                    </span>
                  </Td>
                  <Td>
                    {row.donorName ?? row.donorEmail ?? <span className="text-subtle">—</span>}
                  </Td>
                  <Td className="text-muted">
                    {row.note ? row.note : <span className="text-subtle">—</span>}
                  </Td>
                  <Td className="text-right">
                    {row.deletable ? (
                      <DeleteButton
                        onClick={() => remove(row)}
                        busy={busy === row.id}
                        label={`Remove $${(row.amountCents / 100).toFixed(2)}`}
                      />
                    ) : (
                      <span className="text-[11px] uppercase tracking-[0.14em] text-subtle">
                        Audit only
                      </span>
                    )}
                  </Td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </section>
  );
}
